import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import AccountModal from './AccountModal';

/* Shared account menu — used by the desktop Sidebar (variant="chip")
   and the mobile TopBar (variant="icon"). Opens a blurred-backdrop
   popover card with a name/email header and quick actions. */

function IconShare() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.6 13.5 15.4 17.5" /><path d="M15.4 6.5 8.6 10.5" /></svg>);
}
function IconUser() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="8" r="3.6" /><path d="M4.5 20a7.5 7.5 0 0 1 15 0" /></svg>);
}
function IconSupport() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 4.5 1.5c-.7.7-1.5 1.2-1.5 2.5" /><circle cx="12" cy="17" r=".7" fill="currentColor" /></svg>);
}
function IconLogout() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /><path d="M13 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8" /></svg>);
}
function IconSun() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" /></svg>);
}
function IconMoon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>);
}

// Read/apply the theme with the same mechanism as ThemeToggle / AppearancePills:
// data-theme on <html> + localStorage('soko-theme'), plus the mobile status-bar
// meta color. Kept local so the menu item needs no extra wiring.
function readTheme() {
  try {
    return document.documentElement.getAttribute('data-theme')
      || localStorage.getItem('soko-theme') || 'light';
  } catch { return 'light'; }
}
function applyTheme(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try { localStorage.setItem('soko-theme', theme); } catch { /* ignore */ }
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', theme === 'dark' ? '#0A2831' : '#0B3A4A');
}

export default function AccountMenu({ variant = 'chip', user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const u = user || auth.currentUser || {};
  const email = u.email || '';
  const name = u.displayName || 'Your account';
  const initial = (email[0] || 'S').toUpperCase();

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const go = (to) => { setOpen(false); navigate(to); };
  const doSignOut = () => { setOpen(false); signOut(auth).then(() => navigate('/login')); };

  // Account modal, opened from this menu — or by deep link, so pages that
  // reference the account email (the Alerts page's "wrong address?") can send
  // someone straight to it.
  const [accountOpen, setAccountOpen] = useState(false);
  const openAccount = () => { setOpen(false); setAccountOpen(true); };

  useEffect(() => {
    if (!new URLSearchParams(location.search).has('account')) return;
    // Both variants are always mounted; CSS hides one by viewport. Opening
    // the hidden one would put a modal behind a display:none ancestor, so
    // only the instance actually on screen responds. offsetParent is null
    // exactly when an ancestor is display:none.
    if (wrapRef.current && wrapRef.current.offsetParent === null) return;
    setAccountOpen(true);
    // Consume the parameter so a refresh or a back/forward doesn't reopen it.
    const params = new URLSearchParams(location.search);
    params.delete('account');
    const qs = params.toString();
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
  }, [location.search, location.pathname, navigate]);

  // Quick theme shortcut. (Also available, with more room, in Settings ›
  // Preferences.) Toggling keeps the menu open so the change is visible.
  const [theme, setTheme] = useState(readTheme);
  const isDark = theme === 'dark';
  const toggleTheme = () => {
    const next = isDark ? 'light' : 'dark';
    setTheme(next);
    applyTheme(next);
  };

  return (
    <div className={`account-menu account-menu--${variant}`} ref={wrapRef} data-tour="account-menu">
      {variant === 'icon' ? (
        <button type="button" className="account-menu__avatar-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label="Account">
          <span className="avatar">{initial}</span>
        </button>
      ) : (
        <button type="button" className="sidebar-account" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open}>
          <span className="avatar">{initial}</span>
          <span className="who">
            <span className="name">{name}</span>
            {email && <span className="email">{email}</span>}
          </span>
        </button>
      )}

      {open && (
        <>
          <div className="account-menu__backdrop" onClick={() => setOpen(false)} />
          <div className="account-menu__pop" role="menu">
            <div className="account-menu__head">
              <span className="avatar">{initial}</span>
              <span className="account-menu__who">
                <span className="account-menu__name">{name}</span>
                {email && <span className="account-menu__email">{email}</span>}
              </span>
            </div>
            <div className="account-menu__items">
              {/* Account details (confirm / change the email address) live
                  here rather than on a Settings tab: the address belongs to
                  the account, not to a tank, and someone fixing a sign-up
                  typo looks for their own name in the corner. */}
              <button type="button" className="account-menu-item" role="menuitem" onClick={openAccount}>
                <span className="account-menu-item__icon"><IconUser /></span> Email &amp; account
              </button>
              <button type="button" className="account-menu-item" role="menuitem" onClick={() => go('/settings?tab=sharing')}>
                <span className="account-menu-item__icon"><IconShare /></span> Share access
              </button>
              <button type="button" className="account-menu-item" role="menuitem" onClick={() => go('/help')}>
                <span className="account-menu-item__icon"><IconSupport /></span> Help / Contact support
              </button>
              <button type="button" className="account-menu-item" role="menuitem" onClick={toggleTheme}>
                <span className="account-menu-item__icon">{isDark ? <IconSun /> : <IconMoon />}</span> {isDark ? 'Light mode' : 'Dark mode'}
              </button>
              <div className="account-menu-divider" />
              <button type="button" className="account-menu-item account-menu-item--danger" role="menuitem" onClick={doSignOut}>
                <span className="account-menu-item__icon"><IconLogout /></span> Sign out
              </button>
            </div>
          </div>
        </>
      )}

      <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} user={user} />
    </div>
  );
}
